import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, ShieldCheck, Store, Truck } from "lucide-react";
import LogoCamara from "../assets/LogoCamara.png";
import { MVP_PRODUCTOS } from "../data/mvpProducts.ts";
import { getProductosMarket, type ProductoStore } from "../services/marketplaceService.tsx";

export default function ProductDetailMvp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productoDb, setProductoDb] = useState<ProductoStore | null>(null);
  const [loading, setLoading] = useState(true);
  const productoMvp = MVP_PRODUCTOS.find((p) => String(p.id) === String(id));

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const items = await getProductosMarket();
        const found = (items || []).find((p) => String((p as any).producto_id) === String(id));
        setProductoDb(found || null);
      } catch {
        setProductoDb(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  const producto: any = productoDb || productoMvp;

  return (
    <div className="min-h-screen bg-[#eef6ff]">
      <header className="sticky top-0 z-20 border-b border-sky-200 bg-white/95 backdrop-blur">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-3 px-4 py-3 md:grid-cols-[320px_1fr_220px] sm:px-6">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-left">
            <img src={LogoCamara} alt="Camara Comercial Bolívar" className="h-16 w-16 rounded-lg border border-sky-100 bg-white object-contain p-1" />
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-sky-600">Marketplace Socios</p>
              <h1 className="text-sm font-bold text-slate-800 sm:text-base">Cámara Comercial de Bolívar</h1>
            </div>
          </button>

          <div className="hidden text-sm font-semibold text-slate-600 md:block">Detalle del producto</div>

          <div className="ml-auto flex items-center justify-end gap-2">
            <span className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 sm:text-sm">
              Catálogo informativo
            </span>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-7xl gap-2 overflow-x-auto px-4 pb-3 sm:px-6">
          {["Ofertas", "Tecnología", "Hogar", "Gastronomía", "Servicios", "Industria local"].map((cat) => (
            <span key={cat} className="whitespace-nowrap rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold text-sky-700">
              {cat}
            </span>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="p-6"><div className="mx-auto max-w-5xl rounded-2xl border border-sky-100 bg-white p-6">Cargando producto...</div></div>
      ) : !producto ? (
        <div className="p-6">
          <div className="mx-auto max-w-5xl rounded-2xl border border-sky-100 bg-white p-6 text-center shadow-sm">
            <p className="text-lg font-bold text-slate-800">Producto no encontrado</p>
            <button onClick={() => navigate("/")} className="mt-4 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">
              Volver al inicio
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 sm:p-6">
          <div className="mx-auto max-w-6xl">
            <button onClick={() => navigate("/")} className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-sky-700">
              <ArrowLeft className="h-4 w-4" /> Volver al marketplace
            </button>

            <div className="grid gap-4 rounded-2xl border border-sky-100 bg-white p-4 shadow-sm md:grid-cols-[1.1fr_0.9fr]">
              <div>
                <div className="flex h-[380px] items-center justify-center rounded-xl bg-sky-50">
                  {producto.imagen_url ? (
                    <img src={producto.imagen_url} alt={producto.nombre} className="h-full w-full rounded-xl object-cover" />
                  ) : (
                    <Store className="h-24 w-24 text-sky-300" />
                  )}
                </div>
                <div className="mt-4 rounded-xl border border-sky-100 bg-sky-50 p-4 text-sm text-slate-700">
                  <p className="font-semibold text-slate-900">Descripción</p>
                  <p className="mt-2">{producto.descripcion || 'Sin descripción por el momento.'}</p>
                </div>
              </div>

              <aside className="rounded-xl border border-sky-100 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">Disponible</p>
                <h1 className="mt-1 text-2xl font-bold text-slate-900">{producto.nombre}</h1>
                <p className="mt-1 text-sm text-slate-500">Publicado por {producto.comercio}</p>

                <p className="mt-4 text-4xl font-extrabold text-slate-900">
                  {typeof producto.precio === 'number' ? `$ ${producto.precio.toLocaleString('es-AR')}` : producto.precio}
                </p>
                <p className="text-sm font-semibold text-sky-700">Valor de referencia</p>

                <div className="mt-5 space-y-2 text-sm text-slate-700">
                  <p className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-emerald-600" /> {producto.envio || 'Consultá opciones de entrega en la tienda'}</p>
                  <p className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sky-600" /> Publicación de comercio adherido a la Cámara</p>
                </div>

                <div className="mt-6 rounded-xl border border-sky-200 bg-sky-50 p-4">
                  <p className="text-sm font-semibold text-slate-800">Compra por contacto directo</p>
                  <p className="mt-1 text-sm text-slate-600">
                    Este catálogo funciona como vidriera de productos. Para concretar la compra, contactate directamente con la tienda.
                  </p>
                  <button
                    onClick={() => navigate(-1)}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-bold text-white hover:bg-sky-700"
                  >
                    <MessageCircle className="h-4 w-4" /> Volver a la tienda
                  </button>
                </div>

                <div className="mt-6 flex items-center gap-2 rounded-lg bg-sky-50 p-3">
                  <img src={LogoCamara} alt="Cámara" className="h-8 w-8 object-contain" />
                  <p className="text-xs text-slate-600">Catálogo visual. La operación comercial y la carga de puntos se gestionan en tienda física.</p>
                </div>
              </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
